import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { getSessionTenantId } from '@/lib/auth-helpers';
import type { ContactInsert, ContactUpdate } from '../types';

// Supabase generated types não incluem lat/lng ainda (aguardando db:types pós-geo migration).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ContactInsert) => {
      const tenantId = await getSessionTenantId();
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await db
        .from('contacts')
        .insert({ ...payload, tenant_id: tenantId, created_by: user?.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Contato criado com sucesso!');
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao criar contato: ${e.message}`);
    },
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: ContactUpdate }) => {
      const { data, error } = await db
        .from('contacts')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_: unknown, variables: { id: string; payload: ContactUpdate }) => {
      const { id } = variables;
      toast.success('Contato atualizado!');
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contact', id] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao atualizar: ${e.message}`);
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Soft delete: marca como inativo
      const { error } = await supabase
        .from('contacts')
        .update({ active: false })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Contato removido.');
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao remover: ${e.message}`);
    },
  });
}
